import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

const API_V1  = `${environment.apiUrl}/api/v1`;
const API_V3  = `${environment.apiUrl}/api/v3/api`;

export interface ComplaintPayload {
  complainant_name: string;
  complainant_phone: string;
  complainant_email: string;
  complainant_address: string;
  complaint_type: string;
  priority: string;
  subject_fr: string;
  description_fr: string;
  evidence?: File[];
  captcha_token: string;
  captcha_answer: string;
}

export interface ReportPayload {
  // Coordonnées facultatives → signalement anonyme
  reporter_name?: string;
  reporter_phone?: string;
  reporter_email?: string;
  report_type: string;
  priority: string;
  platform?: string;
  reported_url?: string;
  subject_fr: string;
  description_fr: string;
  reported_content_fr?: string;
  evidence?: File[];
  captcha_token: string;
  captcha_answer: string;
}

export interface ComplaintStatus {
  complaint_number: string;
  complainant_name: string;
  complaint_type: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  priority: string;
  subject: { fr: string; en: string; ar: string };
  created_at: string;
  updated_at: string;
  resolution_notes?: string;
}

export interface DirectoryResult {
  influencers?: { data: any[]; meta: any };
  agencies?: { data: any[]; meta: any };
  approved?: { data: any[]; meta: any };
  stats?: any;
}

@Injectable({ providedIn: 'root' })
export class PublicFormService {
  private http = inject(HttpClient);

  // ── Complaints ────────────────────────────────────────────────────────────

  submitComplaint(payload: ComplaintPayload): Observable<any> {
    const fd = new FormData();
    fd.append('complainant_name',    payload.complainant_name);
    fd.append('complainant_phone',   payload.complainant_phone);
    fd.append('complainant_email',   payload.complainant_email);
    fd.append('complainant_address', payload.complainant_address);
    fd.append('complaint_type',      payload.complaint_type);
    fd.append('priority',            payload.priority);
    fd.append('subject[fr]',         payload.subject_fr);
    fd.append('subject[en]',         '');
    fd.append('subject[ar]',         '');
    fd.append('description[fr]',     payload.description_fr);
    fd.append('description[en]',     '');
    fd.append('description[ar]',     '');
    fd.append('captcha_token',       payload.captcha_token);
    fd.append('captcha_answer',      payload.captcha_answer);
    (payload.evidence ?? []).forEach(f => fd.append('evidence[]', f));
    return this.http.post<any>(`${API_V3}/complaints`, fd).pipe(map(r => r.data));
  }

  trackComplaint(number: string): Observable<ComplaintStatus> {
    return this.http.get<any>(`${API_V3}/complaints/track/${number}`).pipe(map(r => r.data));
  }

  // ── Reports ───────────────────────────────────────────────────────────────

  submitReport(payload: ReportPayload): Observable<any> {
    const fd = new FormData();
    // Coordonnées facultatives → n'envoyer que si renseignées (anonymat)
    if (payload.reporter_name)  fd.append('reporter_name',  payload.reporter_name);
    if (payload.reporter_phone) fd.append('reporter_phone', payload.reporter_phone);
    if (payload.reporter_email) fd.append('reporter_email', payload.reporter_email);
    fd.append('report_type',      payload.report_type);
    fd.append('priority',         payload.priority);
    fd.append('subject[fr]',      payload.subject_fr);
    fd.append('subject[en]',      '');
    fd.append('subject[ar]',      '');
    fd.append('description[fr]',  payload.description_fr);
    fd.append('description[en]',  '');
    fd.append('description[ar]',  '');
    fd.append('reported_content[fr]', payload.reported_content_fr ?? '');
    fd.append('reported_content[en]', '');
    fd.append('reported_content[ar]', '');
    if (payload.platform)     fd.append('reported_platform', payload.platform);
    if (payload.reported_url) fd.append('reported_url', payload.reported_url);
    fd.append('captcha_token',  payload.captcha_token);
    fd.append('captcha_answer', payload.captcha_answer);
    (payload.evidence ?? []).forEach(f => fd.append('evidence[]', f));
    return this.http.post<any>(`${API_V3}/reports`, fd).pipe(map(r => r.data));
  }

  // ── Directory / Annuaire ──────────────────────────────────────────────────

  /**
   * Annuaire public. `page` / `perPage` sont transmis à l'API : sans eux le
   * backend retombe sur son défaut (12 par page) et seule la 1re page revient.
   */
  searchDirectory(
    query: string = '',
    type: string = 'all',
    page: number = 1,
    perPage: number = 50,
  ): Observable<DirectoryResult> {
    const params: any = { type, page, per_page: perPage };
    if (query) params['q'] = query;
    return this.http.get<any>(`${API_V3}/public/directory`, { params }).pipe(map(r => r.data));
  }

  getAgency(id: number | string): Observable<any> {
    return this.http.get<any>(`${API_V3}/public/agencies/${id}`).pipe(map(r => r.data));
  }

  getInfluencer(id: number | string): Observable<any> {
    return this.http.get<any>(`${API_V3}/public/influencers/${id}`).pipe(map(r => r.data));
  }

  getCalligrapher(id: number | string): Observable<any> {
    return this.http.get<any>(`${API_V3}/public/calligraphers/${id}`).pipe(map(r => r.data));
  }
}
